/**
 * Helpers for the ChatGPT Ads Measurement Pixel (OpenAI).
 *
 * The SDK itself is loaded in public/index.html. `window.oaiq` only exists
 * when a Pixel ID is configured there, so every helper is a no-op otherwise.
 *
 * Docs: https://developers.openai.com/ads/measurement-pixel
 */

const oaiq = (...args) => {
  if (typeof window.oaiq !== 'function') {
    return false;
  }
  window.oaiq(...args);
  return true;
};

/**
 * Grant or deny measurement consent. Mirrors the Google consent update
 * triggered by the cookie banner.
 * @param {boolean} granted
 */
export const setOpenAIPixelConsent = (granted) =>
  oaiq('consent', Boolean(granted));

/**
 * Generate a unique ID usable as `event_id`. OpenAI counts events with the
 * same pixel ID + event name + event_id only once, so reusing one ID per
 * conversion protects against accidental double-firing.
 */
export const createOpenAIEventId = () => {
  if (window.crypto && typeof window.crypto.randomUUID === 'function') {
    return window.crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
};

/**
 * Contact form successfully submitted.
 * @param {string} [eventId] Stable ID for this conversion (see createOpenAIEventId)
 */
export const trackOpenAILead = (eventId) =>
  oaiq(
    'measure',
    'lead_created',
    { type: 'customer_action' },
    eventId ? { event_id: eventId } : undefined
  );

/**
 * A demo was actually booked in the embedded Calendly widget.
 * @param {string} [eventId] Stable ID for this booking (Calendly invitee id)
 */
export const trackOpenAIAppointmentScheduled = (eventId) =>
  oaiq(
    'measure',
    'appointment_scheduled',
    { type: 'customer_action' },
    eventId ? { event_id: eventId } : undefined
  );

/**
 * User clicked a Calendly "Termin vereinbaren" link (intent, not a booking;
 * the booking itself is tracked as `appointment_scheduled`).
 */
export const trackOpenAICalendlyClick = () =>
  oaiq(
    'measure',
    'custom',
    { type: 'custom' },
    { custom_event_name: 'calendly_link_click' }
  );

/**
 * Page view (funnel metric, not a conversion).
 * @param {{ id: string, name: string }} page
 */
export const trackOpenAIPageView = ({ id, name }) =>
  oaiq('measure', 'page_viewed', {
    type: 'contents',
    contents: [{ id, name, content_type: 'page' }],
  });

/**
 * User clicked a mailto: link (lead intent).
 */
export const trackOpenAIEmailClick = () =>
  oaiq(
    'measure',
    'custom',
    { type: 'custom' },
    { custom_event_name: 'email_link_click' }
  );

/**
 * User started the product demo video (engagement, not a conversion).
 */
export const trackOpenAIVideoPlay = () =>
  oaiq(
    'measure',
    'custom',
    { type: 'custom' },
    { custom_event_name: 'video_played' }
  );
